// Desc: Home page component
import { Spinner } from "_components";
import { TourType } from "_interfaces";
import { API_ENDPOINT, capitalize, formatCurrency } from "_lib/utils";
import { ConfigurationAtom } from "_states";
import axios from "axios";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

/**
 * ToursPage component
 * @returns {JSX.Element} JSX.Element
 */
const ToursPage = () => {
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [tours, setTours] = useState<TourType[]>([]);
  const configurations = useRecoilValue(ConfigurationAtom);

  useEffect(() => {
    setLoading(true);
    const destination = params.param || "";
    let filter = "";
    if (destination.length > 0) {
      filter = `&filter[destinations][destinations_id][slug][_eq]=${destination}`;
    }
    axios
      .get(
        `${API_ENDPOINT}/items/tours?fields[]=id,name,starting_price,nights,image&filter[status][_eq]=published${filter}&sort=name`
      )
      .then((res) => {
        setTours(res.data.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [params]);

  return (
    <>
      <div
        className="flex flex-col items-center justify-center h-[500px] bg-center bg-cover bg-no-repeat w-full text-white lg:space-y-6 space-y-4 text-center"
        style={{ backgroundImage: "url('/images/destinations-banner.jpg')" }}
      >
        <h1 className="md:text-5xl text-2xl px-2">
          {params && params.param
            ? `Tours for ${capitalize(params.param)}`
            : "Popular tours"}
        </h1>
      </div>

      {/* Popular tours section */}
      <section className="max-w-7xl mx-auto space-y-8 pb-24 md:px-2 sm:px-4 px-4">
        {loading ? (
          <Spinner />
        ) : (
          <div>
            <h2 className="font-semibold text-4xl text-center my-10">
              {tours.length} tours found
            </h2>
            <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-6">
              {tours.map((tour) => (
                <Link
                  key={tour.id}
                  to={`/itinerary/${tour.id}`}
                  className="bg-white border transition ease-in-out duration-200 hover:shadow-lg hover:scale-105 rounded-lg overflow-hidden"
                >
                  <img
                    src={`${API_ENDPOINT}/assets/${tour.image}/${tour.name}.jpg`}
                    alt={tour.name}
                    className="w-full h-52 object-cover object-center"
                  />
                  <div className="p-4 space-y-2">
                    <h3 className="text-xl font-semibold">{tour.name}</h3>
                    <p className="text-sm">
                      {tour.nights + 1} days {tour.nights} nights
                    </p>
                    <p className="text-sm font-semibold">
                      Starting from {formatCurrency(tour.starting_price)} pax
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Top Destinations section */}
      <section className="max-w-7xl mx-auto space-y-8 pb-36 md:px-2 sm:px-4 px-4">
        <div>
          <h2 className="font-semibold text-4xl text-center mb-10">
            Our top destinations
          </h2>
          <div className="grid lg:grid-cols-5 sm:grid-cols-2 grid-cols-1 gap-6">
            {configurations.featured_destinations
              .slice(0, 5)
              .map((destination, key) => (
                <Link
                  key={key}
                  to={`/tours/${destination.slug}`}
                  className="relative flex justify-center items-center rounded-lg overflow-hidden aspect-video md:aspect-square"
                >
                  <div
                    className="h-full w-full transition ease-in-out duration-500 hover:scale-110 bg-center bg-cover hover:brightness-50 brightness-75"
                    style={{
                      backgroundImage: `url(\'${destination.image}\')`,
                    }}
                  ></div>
                  <div className="absolute text-white pointer-events-none">
                    <h3 className="text-2xl">{destination.name}</h3>
                  </div>
                </Link>
              ))}
          </div>
        </div>
        <div className="text-center">
          <Link
            to="/destinations"
            className="bg-primary text-white font-medium px-8 py-3 rounded-lg"
          >
            View all destinations
          </Link>
        </div>
      </section>
    </>
  );
};

export default ToursPage;
